import { useLazyQuery } from "@apollo/client";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import {
  Button,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  addDays,
  addHours,
  endOfDay,
  format,
  startOfDay,
  startOfHour,
  startOfMonth,
} from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import styled from "styled-components";
import { ErrorAlert } from "../../components/ErrorAlert/ErrorAlert";
import Page from "../../components/Page/Page";
import Panel from "../../components/Panel/Panel";
import StatCard from "../../components/StatCard/StatCard";
import Badge from "../../components/Styled/Badge";
import { GenderDistribution, OnboardingHistoryData, OnboardingResult } from "../../graphql/graphql";
import { ONBOARDING_STATS, ONBOARDING_HISTORY } from "../../graphql/queries-gql";
import { CHART_SERIES_COLORS, useChartTheme } from "../../theme/chartTheme";

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
  align-items: start;
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  font-size: 13px;
`;

const StatRowLabel = styled.span<{ $tone?: "green" | "red" }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${(props) =>
    props.$tone ? props.theme.colors[props.$tone] : props.theme.secondaryTextColor};
`;

const StatRowValue = styled.span<{ $tone?: "green" | "red" }>`
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: ${(props) =>
    props.$tone ? props.theme.colors[props.$tone] : props.theme.textColor};
`;

const StatRowMeta = styled.span`
  margin-left: 4px;
  font-weight: 400;
  color: ${(props) => props.theme.secondaryTextColor};
`;

const StatRowDivider = styled.div`
  height: 1px;
  margin: 2px 0;
  background: ${(props) => props.theme.border};
`;

const GenderDot = styled.span<{ $gender: "male" | "female" | "other" }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(props) => {
    if (props.$gender === "male") return props.theme.colors.blue;
    if (props.$gender === "female") return props.theme.colors.primary;
    return props.theme.colors.purple;
  }};
`;

const GenderList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Track = styled.div`
  height: 4px;
  border-radius: 2px;
  background: ${(props) => props.theme.surfaceActive};
  overflow: hidden;
`;

const TrackFill = styled.div<{ $percent: number; $tone: "primary" | "green" }>`
  width: ${(props) => Math.min(Math.max(props.$percent, 0), 100)}%;
  height: 100%;
  border-radius: 2px;
  background: ${(props) => props.theme.colors[props.$tone]};
  transition: width 0.3s ease;
`;

const FunnelRow = styled.div`
  display: grid;
  grid-template-columns: minmax(240px, 300px) minmax(0, 1fr);
  gap: 16px 24px;
  align-items: start;
  padding: 18px;
  border-bottom: 1px solid ${(props) => props.theme.border};

  &:last-child {
    border-bottom: 0;
  }

  @media (max-width: 1000px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const StepInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`;

const StepHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

// Ringed in the step's chart color so the row and its trend line read together
const StepMarker = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid ${(props) => props.$color};
  font-size: 11.5px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${(props) => props.theme.textColor};
  flex-shrink: 0;
`;

const StepTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => props.theme.textColor};
`;

const StepValueRow = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 10px;
`;

const StepCount = styled.span`
  font-size: 26px;
  font-weight: 650;
  line-height: 1.15;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: ${(props) => props.theme.textColor};
`;

const StepShare = styled.span<{ $isPositive: boolean }>`
  font-size: 13px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: ${(props) =>
    props.$isPositive ? props.theme.colors.green : props.theme.colors.red};
`;

const StepGender = styled(GenderList)`
  padding-top: 10px;
  border-top: 1px solid ${(props) => props.theme.border};
`;

const StepChart = styled.div`
  min-width: 0;
`;

const InlineAvg = styled.span`
  display: block;
  margin-top: 6px;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: ${(props) => props.theme.mutedTextColor};
`;

const [BLUE, GREEN, ORANGE, , PURPLE, CYAN] = CHART_SERIES_COLORS;

const STEP_COLORS = {
  atPreferences: ORANGE,
  atMatch: BLUE,
  atReady: PURPLE,
  atAuth: CYAN,
  totalFinishedOnboarding: GREEN,
};

const formatDateLabel = (dateStr: string, hourly: boolean) => {
  try {
    if (hourly) {
      return format(new Date(dateStr), 'MMM d HH:mm');
    }
    const d = new Date(dateStr);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[d.getUTCMonth()]} ${d.getUTCDate()}`;
  } catch {
    return dateStr;
  }
};

const calcAvg = (data: Array<Record<string, unknown>>, key: string, isPercent = false): string => {
  if (data.length === 0) return '0';
  const sum = data.reduce((acc, d) => acc + (Number(d[key]) || 0), 0);
  const avg = sum / data.length;
  return isPercent ? `${avg.toFixed(2)}%` : avg.toFixed(1);
};

const formatShare = (part: number, total: number, digits = 1): string =>
  total > 0 ? ((part / total) * 100).toFixed(digits) : (0).toFixed(digits);

enum DateRange {
  Today,
  Yesterday,
  ThisMonth,
  Last24Hours,
  Last3Days,
  Last7Days,
  Last30Days,
  SpecificDate,
}

const OnboardingFlow = () => {
  const [fromDate, setFromDate] = useState<Date>(startOfDay(new Date()));
  const [platform, setPlatform] = useState<string | undefined>('ios');
  const [toDate, setToDate] = useState<Date>(new Date());
  const [dateRange, setDateRange] = useState<DateRange>(DateRange.Today);
  const chart = useChartTheme();
  const [fetchOnboardingStats, { data, loading, error }] = useLazyQuery<{
    onboardingStats: OnboardingResult;
  }>(ONBOARDING_STATS, {
    fetchPolicy: "network-only",
    variables: {
      args: {
        fromDate,
        toDate,
        platform,
      },
    },
    onCompleted: (data) => {
      console.log("fetchOnboardingStats onCompleted", data);
    },
  });

  const [fetchHistory, { data: historyData }] = useLazyQuery<{
    onboardingHistory: OnboardingHistoryData;
  }>(ONBOARDING_HISTORY, {
    fetchPolicy: "network-only",
    variables: {
      args: { fromDate, toDate, platform },
    },
  });

  useEffect(() => {
    if (!data && !loading && !error) {
      fetchOnboardingStats();
      fetchHistory();
    }
  }, [data, loading, error, fetchOnboardingStats, fetchHistory]);

  const isHourly = dateRange === DateRange.Last24Hours
    || dateRange === DateRange.Today
    || dateRange === DateRange.Yesterday
    || dateRange === DateRange.SpecificDate;

  const chartData = useMemo(() => {
    if (!historyData?.onboardingHistory?.onboardingData) return [];

    const byHour = new Map<string, {
      date: string;
      totalUsers: number;
      atPreferences: number;
      atMatch: number;
      atReady: number;
      atAuth: number;
      totalFinishedOnboarding: number;
    }>();

    for (const d of historyData.onboardingHistory.onboardingData) {
      const hourKey = startOfHour(new Date(d.date)).toISOString();
      const existing = byHour.get(hourKey);
      if (existing) {
        existing.totalUsers += d.totalUsers;
        existing.atPreferences += (d as any).atPreferences || 0;
        existing.atMatch += (d as any).atMatch || 0;
        existing.atReady += (d as any).atReady || 0;
        existing.atAuth += (d as any).atAuth || 0;
        existing.totalFinishedOnboarding += d.totalFinishedOnboarding;
      } else {
        byHour.set(hourKey, {
          date: hourKey,
          totalUsers: d.totalUsers,
          atPreferences: (d as any).atPreferences || 0,
          atMatch: (d as any).atMatch || 0,
          atReady: (d as any).atReady || 0,
          atAuth: (d as any).atAuth || 0,
          totalFinishedOnboarding: d.totalFinishedOnboarding,
        });
      }
    }

    const byTimestamp = new Map<string, typeof byHour extends Map<string, infer V> ? V : never>();

    if (isHourly) {
      byHour.forEach((val, key) => byTimestamp.set(key, val));
    } else {
      byHour.forEach((entry) => {
        const d = new Date(entry.date);
        const dayKey = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())).toISOString();
        const existing = byTimestamp.get(dayKey);
        if (existing) {
          existing.totalUsers += entry.totalUsers;
          existing.atPreferences += entry.atPreferences;
          existing.atMatch += entry.atMatch;
          existing.atReady += entry.atReady;
          existing.atAuth += entry.atAuth;
          existing.totalFinishedOnboarding += entry.totalFinishedOnboarding;
        } else {
          byTimestamp.set(dayKey, { ...entry, date: dayKey });
        }
      });
    }

    return Array.from(byTimestamp.values())
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .map((d) => ({
        dateLabel: formatDateLabel(d.date, isHourly),
        totalUsers: d.totalUsers,
        atPreferences: d.atPreferences,
        atMatch: d.atMatch,
        atReady: d.atReady,
        atAuth: d.atAuth,
        totalFinishedOnboarding: d.totalFinishedOnboarding,
        preferencesRate: d.totalUsers > 0 ? Number(((d.atPreferences / d.totalUsers) * 100).toFixed(1)) : 0,
        matchRate: d.totalUsers > 0 ? Number(((d.atMatch / d.totalUsers) * 100).toFixed(1)) : 0,
        readyRate: d.totalUsers > 0 ? Number(((d.atReady / d.totalUsers) * 100).toFixed(1)) : 0,
        authRate: d.totalUsers > 0 ? Number(((d.atAuth / d.totalUsers) * 100).toFixed(1)) : 0,
        completionRate: d.totalUsers > 0 ? Number(((d.totalFinishedOnboarding / d.totalUsers) * 100).toFixed(1)) : 0,
      }));
  }, [historyData, isHourly]);

  const exportToCsv = useCallback(() => {
    const stats = data?.onboardingStats;
    const lines: string[] = [];

    // Summary section
    lines.push('Onboarding Flow Analytics');
    lines.push(`Platform,${platform ?? 'all'}`);
    lines.push(`From,${fromDate.toISOString()}`);
    lines.push(`To,${toDate.toISOString()}`);
    lines.push('');

    if (stats) {
      lines.push('Summary');
      lines.push('Metric,Value');
      lines.push(`Total Users,${stats.totalUsers}`);
      lines.push(`Total Push Activated,${stats.totalPushActivated}`);
      lines.push(`At Matching Preferences,${stats.atMatchingPreferences.totalUsers}`);
      lines.push(`Active At Matching Preferences,${stats.activeAtMatchingPreferences}`);
      lines.push(`At Match,${stats.atMatch.totalUsers}`);
      lines.push(`Active At Match,${stats.activeAtMatch}`);
      lines.push(`At Ready,${stats.atReady.totalUsers}`);
      lines.push(`Active At Ready,${stats.activeAtReady}`);
      lines.push(`At Auth,${stats.atAuth.totalUsers}`);
      lines.push(`Active At Auth,${stats.activeAtAuth}`);
      lines.push(`Finished Onboarding,${stats.finishedOnboarding.totalUsers}`);
      lines.push(`Finished With Photo,${stats.finishedOnboardingWithPhoto}`);
      lines.push(`Finished With Photo Male,${stats.finishedOnboardingWithPhotoMale}`);
      lines.push(`Finished With Photo Female,${stats.finishedOnboardingWithPhotoFemale}`);
      lines.push(`Finished With Location,${stats.finishedOnboardingWithLocation}`);
      lines.push(`Rewarded Ads Watched,${stats.totalRewardedAdsWatched}`);
      lines.push(`Premium Total,${stats.premiumTotal}`);
      lines.push(`Premium Onboarding,${stats.premiumOnboarding}`);
      lines.push(`Premium Without Flag,${stats.premiumWithoutFlag}`);
      lines.push('');
    }

    // Historical data section
    if (chartData.length > 0) {
      lines.push('Historical Data');
      lines.push('Date,Total Users,At Preferences,At Preferences %,At Match,At Match %,At Ready,At Ready %,At Auth,At Auth %,Finished Onboarding,Completion %');
      for (const row of chartData) {
        lines.push([
          row.dateLabel,
          row.totalUsers,
          row.atPreferences,
          row.preferencesRate,
          row.atMatch,
          row.matchRate,
          row.atReady,
          row.readyRate,
          row.atAuth,
          row.authRate,
          row.totalFinishedOnboarding,
          row.completionRate,
        ].join(','));
      }
    }

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `onboarding-flow-${platform ?? 'all'}-${format(fromDate, 'yyyy-MM-dd')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [data, chartData, platform, fromDate, toDate]);

  const renderInlineChart = (
    countKey: string,
    rateKey: string,
    color: string,
  ) => (
    <StepChart>
      <ResponsiveContainer width="100%" height={160}>
        <LineChart data={chartData}>
          <CartesianGrid {...chart.grid} />
          <XAxis dataKey="dateLabel" {...chart.axis} fontSize={10} tick={{ fontSize: 10 }} />
          <YAxis yAxisId="left" {...chart.axis} fontSize={10} width={35} />
          <YAxis yAxisId="right" orientation="right" {...chart.axis} fontSize={10} width={35} tickFormatter={(v) => `${v}%`} />
          <Tooltip {...chart.tooltip} formatter={(value: number, name: string) => name.includes('%') ? [`${value}%`, name] : [value, name]} />
          <Line yAxisId="left" type="monotone" dataKey={countKey} name="Count" stroke={color} strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} />
          <Line yAxisId="right" type="monotone" dataKey={rateKey} name="% of total" stroke={color} strokeWidth={1.5} strokeOpacity={0.5} strokeDasharray="4 4" dot={false} activeDot={{ r: 3 }} />
        </LineChart>
      </ResponsiveContainer>
      <InlineAvg>
        Avg: {calcAvg(chartData, countKey)} &middot; Avg %: {calcAvg(chartData, rateKey, true)}
      </InlineAvg>
    </StepChart>
  );

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
    switch (range) {
      case DateRange.Today:
        setFromDate(startOfDay(new Date()));
        setToDate(new Date());
        break;
      case DateRange.Yesterday:
        setFromDate(startOfDay(addDays(new Date(), -1)));
        setToDate(endOfDay(addDays(new Date(), -1)));
        break;
      case DateRange.ThisMonth:
        setFromDate(startOfMonth(new Date()));
        setToDate(new Date());
        break;
      case DateRange.Last24Hours:
        setFromDate(addHours(new Date(), -24));
        setToDate(new Date());
        break;
      case DateRange.Last3Days:
        setFromDate(addDays(new Date(), -3));
        setToDate(new Date());
        break;
      case DateRange.Last7Days:
        setFromDate(addDays(new Date(), -7));
        setToDate(new Date());
        break;
      case DateRange.Last30Days:
        setFromDate(addDays(new Date(), -30));
        setToDate(new Date());
        break;
      case DateRange.SpecificDate:
        break;
    }
  };

  const handleSpecificDateChange = (date: Date | null) => {
    if (date) {
      setDateRange(DateRange.SpecificDate);
      setFromDate(startOfDay(date));
      setToDate(endOfDay(date));
    }
  };

  const renderGenderRows = (
    genderDist: GenderDistribution,
    total: number,
  ) => {
    if (total === 0) return null;

    return (
      <>
        {genderDist.maleUsers > 0 && (
          <StatRow>
            <StatRowLabel>
              <GenderDot $gender="male" />
              Male
            </StatRowLabel>
            <StatRowValue>
              {genderDist.maleUsers}
              <StatRowMeta>({formatShare(genderDist.maleUsers, total, 0)}%)</StatRowMeta>
            </StatRowValue>
          </StatRow>
        )}
        {genderDist.femaleUsers > 0 && (
          <StatRow>
            <StatRowLabel>
              <GenderDot $gender="female" />
              Female
            </StatRowLabel>
            <StatRowValue>
              {genderDist.femaleUsers}
              <StatRowMeta>({formatShare(genderDist.femaleUsers, total, 0)}%)</StatRowMeta>
            </StatRowValue>
          </StatRow>
        )}
        {genderDist.otherGenderUsers > 0 && (
          <StatRow>
            <StatRowLabel>
              <GenderDot $gender="other" />
              Other
            </StatRowLabel>
            <StatRowValue>
              {genderDist.otherGenderUsers}
              <StatRowMeta>({formatShare(genderDist.otherGenderUsers, total, 0)}%)</StatRowMeta>
            </StatRowValue>
          </StatRow>
        )}
      </>
    );
  };

  const renderStepRow = (
    step: number,
    title: string,
    genderDist: GenderDistribution,
    totalUsers: number,
    activeCount: number | undefined,
    countKey: string,
    rateKey: string,
    color: string,
  ) => {
    const count = genderDist.totalUsers;
    const percentage = formatShare(count, totalUsers);

    return (
      <FunnelRow key={countKey}>
        <StepInfo>
          <StepHeader>
            <StepMarker $color={color}>{step}</StepMarker>
            <StepTitle>{title}</StepTitle>
          </StepHeader>
          <StepValueRow>
            <StepCount>{count}</StepCount>
            <StepShare $isPositive={count > 0}>{percentage}% of total</StepShare>
            {activeCount !== undefined && activeCount > 0 && (
              <Badge $tone="green">{activeCount} active</Badge>
            )}
          </StepValueRow>
          <Track>
            <TrackFill $percent={Number(percentage)} $tone="primary" />
          </Track>
          {count > 0 && (
            <StepGender>{renderGenderRows(genderDist, count)}</StepGender>
          )}
        </StepInfo>
        {chartData.length > 0 && renderInlineChart(countKey, rateKey, color)}
      </FunnelRow>
    );
  };

  const renderLoadingState = () => (
    <>
      <SummaryGrid>
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} variant="rounded" height={200} />
        ))}
      </SummaryGrid>
      <Skeleton variant="rounded" height={560} />
    </>
  );

  const stats = data?.onboardingStats;
  const finishedUsers = stats?.finishedOnboarding.totalUsers ?? 0;

  return (
    <Page
      title="Onboarding flow"
      description="Users created in the selected period, by the onboarding step they have reached."
      actions={
        <>
          <ToggleButtonGroup
            value={platform}
            exclusive
            onChange={(_, value) => setPlatform(value)}
            size="small"
          >
            <ToggleButton value="ios">iOS</ToggleButton>
            <ToggleButton value="android">Android</ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            onClick={exportToCsv}
            disabled={!data}
            startIcon={<ArrowDownTrayIcon width={16} height={16} />}
          >
            Export CSV
          </Button>
        </>
      }
      toolbar={
        <>
          <ToggleButtonGroup
            value={dateRange}
            exclusive
            onChange={(_, value) =>
              value !== null && handleDateRangeChange(value)
            }
            size="small"
          >
            <ToggleButton value={DateRange.Today}>Today</ToggleButton>
            <ToggleButton value={DateRange.Yesterday}>Yesterday</ToggleButton>
            <ToggleButton value={DateRange.ThisMonth}>This month</ToggleButton>
            <ToggleButton value={DateRange.Last24Hours}>24 hours</ToggleButton>
            <ToggleButton value={DateRange.Last3Days}>3 days</ToggleButton>
            <ToggleButton value={DateRange.Last7Days}>7 days</ToggleButton>
            <ToggleButton value={DateRange.Last30Days}>30 days</ToggleButton>
          </ToggleButtonGroup>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Pick a date"
              value={dateRange === DateRange.SpecificDate ? fromDate : null}
              onChange={handleSpecificDateChange}
              disableFuture
              slotProps={{
                textField: {
                  size: "small",
                  sx: { width: 180 },
                },
              }}
            />
          </LocalizationProvider>
        </>
      }
    >
      {error && <ErrorAlert error={error} />}

      {loading
        ? renderLoadingState()
        : stats && (
            <>
              <SummaryGrid>
                <StatCard label="Total users" value={stats.totalUsers}>
                  {renderGenderRows(stats.totalDistribution, stats.totalUsers)}
                  <StatRow>
                    <StatRowLabel>Push active</StatRowLabel>
                    <StatRowValue>
                      {stats.totalPushActivated}
                      <StatRowMeta>
                        ({formatShare(stats.totalPushActivated, stats.totalUsers, 0)}%)
                      </StatRowMeta>
                    </StatRowValue>
                  </StatRow>
                </StatCard>

                <StatCard label="Finished onboarding" value={finishedUsers}>
                  <StatRow>
                    <StatRowLabel>With photo</StatRowLabel>
                    <StatRowValue>
                      {stats.finishedOnboardingWithPhoto}
                      {finishedUsers > 0 && (
                        <StatRowMeta>
                          ({formatShare(stats.finishedOnboardingWithPhoto, finishedUsers)}%)
                        </StatRowMeta>
                      )}
                    </StatRowValue>
                  </StatRow>
                  <StatRow>
                    <StatRowLabel>With photo, male</StatRowLabel>
                    <StatRowValue>
                      {stats.finishedOnboardingWithPhotoMale}
                      {finishedUsers > 0 && (
                        <StatRowMeta>
                          ({formatShare(stats.finishedOnboardingWithPhotoMale, finishedUsers)}%)
                        </StatRowMeta>
                      )}
                    </StatRowValue>
                  </StatRow>
                  <StatRow>
                    <StatRowLabel>With photo, female</StatRowLabel>
                    <StatRowValue>
                      {stats.finishedOnboardingWithPhotoFemale}
                      {finishedUsers > 0 && (
                        <StatRowMeta>
                          ({formatShare(stats.finishedOnboardingWithPhotoFemale, finishedUsers)}%)
                        </StatRowMeta>
                      )}
                    </StatRowValue>
                  </StatRow>
                  <StatRow>
                    <StatRowLabel>With location</StatRowLabel>
                    <StatRowValue>
                      {stats.finishedOnboardingWithLocation}
                      {finishedUsers > 0 && (
                        <StatRowMeta>
                          ({formatShare(stats.finishedOnboardingWithLocation, finishedUsers)}%)
                        </StatRowMeta>
                      )}
                    </StatRowValue>
                  </StatRow>
                  <StatRow>
                    <StatRowLabel>Rewarded ads watched</StatRowLabel>
                    <StatRowValue>{stats.totalRewardedAdsWatched}</StatRowValue>
                  </StatRow>
                  <StatRowDivider />
                  <StatRow>
                    <StatRowLabel>Overall completion</StatRowLabel>
                    <StatRowValue $tone="green">
                      {formatShare(finishedUsers, stats.totalUsers)}%
                    </StatRowValue>
                  </StatRow>
                  <Track>
                    <TrackFill
                      $percent={
                        stats.totalUsers > 0
                          ? (finishedUsers / stats.totalUsers) * 100
                          : 0
                      }
                      $tone="green"
                    />
                  </Track>
                </StatCard>

                <StatCard label="Premium" value={stats.premiumTotal}>
                  <StatRow>
                    <StatRowLabel>Total CR</StatRowLabel>
                    <StatRowValue>
                      {formatShare(stats.premiumTotal, stats.totalUsers)}%
                    </StatRowValue>
                  </StatRow>
                  <StatRow>
                    <StatRowLabel>Finished onboarding CR</StatRowLabel>
                    <StatRowValue>
                      {formatShare(stats.premiumTotal, finishedUsers)}%
                    </StatRowValue>
                  </StatRow>
                  <StatRow>
                    <StatRowLabel>Onboarding paywall</StatRowLabel>
                    <StatRowValue>
                      {stats.premiumOnboarding}
                      <StatRowMeta>
                        ({formatShare(stats.premiumOnboarding, finishedUsers)}%)
                      </StatRowMeta>
                    </StatRowValue>
                  </StatRow>
                  {stats.premiumWithoutFlag > 0 && (
                    <>
                      <StatRowDivider />
                      <StatRow>
                        <StatRowLabel $tone="red">
                          Purchased without premium flag
                        </StatRowLabel>
                        <StatRowValue $tone="red">
                          {stats.premiumWithoutFlag}
                        </StatRowValue>
                      </StatRow>
                    </>
                  )}
                </StatCard>
              </SummaryGrid>

              <Panel
                title="Onboarding funnel"
                description="Share of total users at each step, with the trend over the selected period"
                padding={0}
              >
                {renderStepRow(
                  1,
                  "At matching preferences",
                  stats.atMatchingPreferences,
                  stats.totalUsers,
                  stats.activeAtMatchingPreferences,
                  'atPreferences',
                  'preferencesRate',
                  STEP_COLORS.atPreferences,
                )}
                {renderStepRow(
                  2,
                  "At match",
                  stats.atMatch,
                  stats.totalUsers,
                  stats.activeAtMatch,
                  'atMatch',
                  'matchRate',
                  STEP_COLORS.atMatch,
                )}
                {renderStepRow(
                  3,
                  "At ready",
                  stats.atReady,
                  stats.totalUsers,
                  stats.activeAtReady,
                  'atReady',
                  'readyRate',
                  STEP_COLORS.atReady,
                )}
                {renderStepRow(
                  4,
                  "At auth",
                  stats.atAuth,
                  stats.totalUsers,
                  stats.activeAtAuth,
                  'atAuth',
                  'authRate',
                  STEP_COLORS.atAuth,
                )}
                {renderStepRow(
                  5,
                  "Finished onboarding",
                  stats.finishedOnboarding,
                  stats.totalUsers,
                  undefined,
                  'totalFinishedOnboarding',
                  'completionRate',
                  STEP_COLORS.totalFinishedOnboarding,
                )}
              </Panel>
            </>
          )}
    </Page>
  );
};

export default OnboardingFlow;
